import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ACHValidators, CreditCardValidators } from '@arc/aureus';
import {
  AddPaymentMethodForm,
  AureusAchForm,
  AureusAddressForm,
  AureusCreditCardForm,
  PaymentMethodFormDetails,
  PaymentMethodInformation,
  PaymentMethodType,
} from '../models';

export const buildCreditCardGroup = (): AureusCreditCardForm =>
  new FormGroup(
    {
      name: new FormControl('', CreditCardValidators.defaultNameOnAccount),
      number: new FormControl('', CreditCardValidators.defaultCreditCardNumber),
      expiration: new FormControl('', CreditCardValidators.defaultExpiration),
      expirationMonth: new FormControl(''),
      expirationYear: new FormControl(''),
      type: new FormControl('', CreditCardValidators.defaultType),
    },
    { validators: CreditCardValidators.defaultGroup },
  );

// Disabled card number and expiration controls for Worldpay Express flow
export const buildRemoteCardCapturedGroup = (): AureusCreditCardForm =>
  new FormGroup({
    name: new FormControl('', CreditCardValidators.defaultNameOnAccount),
    number: new FormControl({ value: '', disabled: true }),
    expiration: new FormControl(
      { value: '', disabled: true },
      CreditCardValidators.defaultExpiration,
    ),
    expirationMonth: new FormControl({ value: '', disabled: true }),
    expirationYear: new FormControl({ value: '', disabled: true }),
    type: new FormControl(''),
  });

export const buildAddressGroup = (): AureusAddressForm =>
  new FormGroup({
    country: new FormControl('', Validators.required),
    address1: new FormControl('', Validators.required),
    address2: new FormControl(''),
    city: new FormControl('', Validators.required),
    state: new FormControl('', Validators.required),
    postalCode: new FormControl('', Validators.required),
  });

export const buildAchForm = (): AureusAchForm =>
  new FormGroup(
    {
      accountNumber: new FormControl('', { nonNullable: true }),
      accountType: new FormControl('', { nonNullable: true }),
      bankAccountType: new FormControl('', { nonNullable: true }),
      confirmAccountNumber: new FormControl('', { nonNullable: true }),
      institutionNumber: new FormControl('', { nonNullable: true }),
      nameOnAccount: new FormControl('', { nonNullable: true }),
      region: new FormControl('', { nonNullable: true }),
      routingNumber: new FormControl('', { nonNullable: true }),
      transitNumber: new FormControl('', { nonNullable: true }),
      entityType: new FormControl('', { nonNullable: true }),
    },
    {
      validators: [
        ACHValidators.groupDelegateControlValidators(
          'accountNumber',
          ACHValidators.defaultAccountNumber,
        ),
        ACHValidators.groupDelegateControlValidators(
          'bankAccountType',
          ACHValidators.defaultBankAccountType,
        ),
        ACHValidators.groupDelegateControlValidators(
          'nameOnAccount',
          ACHValidators.defaultNameOnAccount,
        ),
        ACHValidators.confirmAccountNumber,
      ],
    },
  );

export const buildAddPaymentForm = (): AddPaymentMethodForm =>
  new FormGroup({
    billingInformation: new FormGroup({
      address: buildAddressGroup(),
    }),
    selectedPaymentMethod: new FormControl(null),
    achInfoDetails: buildAchForm(),
    creditCard: buildCreditCardGroup(),
    savePaymentAccount: new FormControl(false),
  });

export const getPaymentMethodFormDetails = (
  form: AddPaymentMethodForm,
): PaymentMethodFormDetails => {
  const selectedPaymentMethod = form.controls.selectedPaymentMethod.value;
  const paymentMethodInformation: PaymentMethodInformation =
    selectedPaymentMethod === PaymentMethodType.ACH
      ? ({
          kind: 'ach',
          ...form.controls.achInfoDetails.getRawValue(),
        } as PaymentMethodInformation)
      : ({
          kind: 'creditCard',
          ...form.controls.creditCard.getRawValue(),
        } as PaymentMethodInformation);
  return {
    billingInformation: form.controls.billingInformation.getRawValue(),
    savePaymentAccount: form.controls.savePaymentAccount.value,
    paymentMethodInformation,
  };
};
